// Severity / category metadata, provenance tags, confidence meter, and the
// defect edit form — shared across the viewer, list, table, and popovers.
// The pure metadata (SEVERITY/CATEGORY tables and helpers) lives in
// defect-meta.ts so the Worker-side report generator can import it without
// dragging React/Leaflet; it is re-exported here for existing call sites.
import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { CATEGORY, CAT_FAMILIES, SEVERITY, SEV_ORDER, sevMeta } from "./defect-meta";
import { defectAreaM2, fmtArea } from "./geo";
import { IcCheck, IcChev, IcEdit, IcPlus } from "./icons";
import type { Defect, DefectCategory, DefectUserRef, Severity } from "./types";

export {
  CATEGORY, CAT_FAMILIES, CAT_ORDER, catLabel, catOf, SEVERITY, SEV_ORDER, sevMeta,
  type SeverityMeta,
} from "./defect-meta";

export function SeverityPill({ sev, size = "md" }: { sev: Severity; size?: "sm" | "md" }) {
  const m = sevMeta(sev);
  const sm = size === "sm";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "var(--font-mono)", fontSize: sm ? 9 : 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: m.color, background: m.bg, border: `1px solid ${m.bc}`, borderRadius: 6, padding: sm ? "2px 7px" : "3px 9px", whiteSpace: "nowrap" }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: m.color }}></span>{m.label}
    </span>
  );
}

// Neutral mono chip — severity keeps the colour, type stays quiet.
export function CategoryTag({ cat, size = "md" }: { cat: DefectCategory; size?: "sm" | "md" }) {
  const m = CATEGORY[cat] ?? CATEGORY.other;
  const sm = size === "sm";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", fontFamily: "var(--font-mono)", fontSize: sm ? 9 : 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--di-muted-2)", background: "var(--di-ink)", border: "1px solid var(--di-border)", borderRadius: 6, padding: sm ? "2px 7px" : "3px 9px", whiteSpace: "nowrap" }}>{m.label}</span>
  );
}

// Type filter — 19 categories across families, so it's a grouped,
// scrollable dropdown with a family section header before each group.
function CatOption({ label, on, count, onClick }: { label: string; on: boolean; count?: number; onClick: () => void }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 9px", borderRadius: 8, border: "none", background: on ? "rgba(249,115,22,0.10)" : h ? "rgba(255,255,255,0.05)" : "transparent", color: on ? "var(--di-orange)" : "var(--di-text)", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 500, textAlign: "left", transition: "background 120ms ease" }}>
      <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      {count != null && <span style={{ opacity: 0.6, flexShrink: 0 }}>{count}</span>}
      {on && <IcCheck size={11} color="var(--di-orange)" sw={3} />}
    </button>
  );
}
function CatFamilyHead({ children }: { children: ReactNode }) {
  return <div style={{ padding: "9px 9px 4px", fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--di-subtle)", whiteSpace: "nowrap" }}>{children}</div>;
}

// Grouped category select for the defect form (full-width, opens a
// scrollable family-grouped list). Replaces the wall of type buttons.
export function CatSelect({ value, setValue }: { value: string; setValue: (c: DefectCategory) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const cur = CATEGORY[value as DefectCategory] ?? CATEGORY.other;
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 12px", borderRadius: 9, border: `1px solid ${open ? "var(--di-orange)" : "var(--di-border)"}`, background: "var(--di-navy)", color: "var(--di-text)", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 13.5, fontWeight: 500, textAlign: "left" }}>
        <span style={{ flex: 1 }}>{cur.label}</span>
        <IcChev size={13} color="var(--di-subtle)" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms ease" }} />
      </button>
      {open &&
        <div className="pop-in" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 700, maxHeight: 300, overflowY: "auto", background: "var(--di-surface)", border: "1px solid var(--di-border)", borderRadius: 12, boxShadow: "0 18px 44px rgba(0,0,0,0.55)", padding: 5, display: "flex", flexDirection: "column", gap: 1 }}>
          {CAT_FAMILIES.map((fam) => (
            <Fragment key={fam.id}>
              <CatFamilyHead>{fam.label}</CatFamilyHead>
              {fam.cats.map((c) => <CatOption key={c} label={CATEGORY[c].label} on={(value || "other") === c} onClick={() => { setValue(c); setOpen(false); }} />)}
            </Fragment>
          ))}
        </div>}
    </div>
  );
}

export type CatFilterValue = DefectCategory | "all";
export function CatFilter({ value, setValue, counts }: {
  value: CatFilterValue;
  setValue: (v: CatFilterValue) => void;
  counts: Partial<Record<string, number>>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const cur = value && value !== "all" ? CATEGORY[value] : null;
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 7, border: `1px solid ${cur ? "var(--di-orange)" : open ? "var(--di-input)" : "var(--di-border)"}`, background: cur ? "rgba(249,115,22,0.10)" : "transparent", color: cur ? "var(--di-orange)" : "var(--di-muted)", cursor: "pointer", fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>
        {cur ? cur.label : "Type"}
        {cur && <span style={{ opacity: 0.6 }}>{counts[value] != null ? counts[value] : 0}</span>}
        <IcChev size={11} color={cur ? "var(--di-orange)" : "var(--di-subtle)"} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 150ms ease" }} />
      </button>
      {open &&
        <div className="pop-in" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 700, width: 232, maxHeight: 380, overflowY: "auto", background: "var(--di-surface)", border: "1px solid var(--di-border)", borderRadius: 12, boxShadow: "0 18px 44px rgba(0,0,0,0.55)", padding: 5, display: "flex", flexDirection: "column", gap: 1 }}>
          <CatOption label="All types" on={(value || "all") === "all"} count={counts.all} onClick={() => { setValue("all"); setOpen(false); }} />
          {CAT_FAMILIES.map((f) => (
            <div key={f.id}>
              <div style={{ padding: "7px 9px 3px", fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--di-faint)" }}>{f.label}</div>
              {f.cats.map((c) => <CatOption key={c} label={CATEGORY[c].label} on={value === c} count={counts[c]} onClick={() => { setValue(c); setOpen(false); }} />)}
            </div>
          ))}
        </div>}
    </div>
  );
}

// ── Severity filter chips ─────────────────────────────────────────────
export type SevFilterValue = Severity | "all";
export function SevFilter({ value, setValue, counts }: {
  value: SevFilterValue;
  setValue: (v: SevFilterValue) => void;
  counts: Partial<Record<string, number>>;
}) {
  const opts: { id: SevFilterValue; label: string }[] = [{ id: "all", label: "All" }, ...SEV_ORDER.map((s) => ({ id: s, label: SEVERITY[s].label }))];
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {opts.map((o) => {
        const on = (value || "all") === o.id;
        const c = o.id === "all" ? "var(--di-orange)" : SEVERITY[o.id as Severity].color;
        return (
          <button key={o.id} onClick={() => setValue(o.id)}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 7, border: `1px solid ${on ? c : "var(--di-border)"}`, background: on ? (o.id === "all" ? "rgba(249,115,22,0.10)" : SEVERITY[o.id as Severity].bg) : "transparent", color: on ? c : "var(--di-muted)", cursor: "pointer", fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {o.id !== "all" && <span style={{ width: 6, height: 6, borderRadius: "50%", background: c }}></span>}
            {o.label}<span style={{ opacity: 0.6 }}>{counts[o.id] != null ? counts[o.id] : 0}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Provenance: AI / Edited / Added tag — shows WHO, resolved live ────
let DEFECT_USERS: Record<string, DefectUserRef> = {};
export function setDefectUsers(list: DefectUserRef[] | null | undefined) {
  DEFECT_USERS = {};
  (list || []).forEach((u) => { if (u && u.id != null) DEFECT_USERS[u.id] = u; });
}
export function userName(id: string | null | undefined): string | null {
  if (id == null) return null;
  const u = DEFECT_USERS[id];
  return u && u.name ? u.name : "Unknown";
}
export function SourceTag({ d }: { d: Defect }) {
  if (d.source !== "user" && !d.modified) {
    return <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "#8A93A2", background: "var(--di-ink)", border: "1px solid var(--di-border)", borderRadius: 5, padding: "2px 6px", whiteSpace: "nowrap" }}>AI</span>;
  }
  const added = d.source === "user";
  const who = userName(added ? d.addedBy : d.editedBy);
  const unknown = who === "Unknown";
  const color = added ? "#6AA6FF" : "#F5B544";
  const bg = added ? "rgba(106,166,255,0.12)" : "rgba(245,181,68,0.12)";
  const bc = added ? "rgba(106,166,255,0.30)" : "rgba(245,181,68,0.30)";
  const Icon = added ? IcPlus : IcEdit;
  return (
    <span title={(added ? "Added by " : "Edited by ") + who} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700, letterSpacing: "0.03em", color: unknown ? "var(--di-subtle)" : color, background: unknown ? "var(--di-ink)" : bg, border: `1px solid ${unknown ? "var(--di-border)" : bc}`, borderRadius: 5, padding: "2px 6px 2px 5px", whiteSpace: "nowrap" }}>
      <Icon size={9} color={unknown ? "var(--di-subtle)" : color} sw={2.4} />{who}
    </span>
  );
}

export const dotRing = (d: Defect): string | null => (d.source === "user" ? "#6AA6FF" : d.modified ? "#F5B544" : null);

// ── AI confidence ─────────────────────────────────────────────────────
export function confidenceColor(pct: number): string {
  if (pct >= 85) return "var(--di-success)";
  if (pct >= 70) return "#F5B544";
  return "var(--di-subtle)";
}
export function ConfidenceMeter({ value, variant = "full" }: { value?: number | null; variant?: "full" | "row" }) {
  const pct = Math.max(0, Math.min(100, Math.round(value == null ? 0 : value)));
  const col = confidenceColor(pct);
  if (variant === "row") {
    return (
      <div title={`AI confidence · ${pct}%`} style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, flexShrink: 0, width: 74 }}>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 7.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--di-subtle)", lineHeight: 1, whiteSpace: "nowrap" }}>AI confidence</span>
        <div style={{ display: "flex", alignItems: "center", gap: 5, width: "100%" }}>
          <div style={{ flex: 1, height: 3, borderRadius: 999, background: "var(--di-ink)", overflow: "hidden" }}>
            <div style={{ width: pct + "%", height: "100%", background: col, borderRadius: 999 }}></div>
          </div>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700, color: col, lineHeight: 1 }}>{pct}%</span>
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--di-subtle)", whiteSpace: "nowrap" }}>AI confidence</span>
      <div style={{ flex: 1, height: 5, borderRadius: 999, background: "var(--di-ink)", overflow: "hidden" }}>
        <div style={{ width: pct + "%", height: "100%", background: col, borderRadius: 999 }}></div>
      </div>
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, color: col }}>{pct}%</span>
    </div>
  );
}

// ── Plan-area tag for outlined defects ────────────────────────────────
export function AreaTag({ d, size = "md" }: { d: Defect; size?: "sm" | "md" }) {
  // No plan area for an ad hoc photo defect: a photo has no georeference.
  if (!d || !d.shape || d.view) return null;
  const sm = size === "sm";
  const area = defectAreaM2(d);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: "var(--font-mono)", fontSize: sm ? 9 : 10, fontWeight: 700, letterSpacing: "0.07em", color: "var(--di-muted-2)", background: "var(--di-ink)", border: "1px solid var(--di-border)", borderRadius: 6, padding: sm ? "2px 7px" : "3px 9px", whiteSpace: "nowrap" }}>
      <span style={{ textTransform: "uppercase", color: "var(--di-subtle)" }}>Plan area</span>{area != null ? fmtArea(area) : "—"}
    </span>
  );
}

// ── Editor form bits ──────────────────────────────────────────────────
export function fieldBox(extra?: CSSProperties): CSSProperties {
  return { width: "100%", padding: "10px 12px", borderRadius: 9, border: "1px solid var(--di-border)", background: "var(--di-navy)", outline: "none", color: "var(--di-text)", fontFamily: "var(--font-sans)", fontSize: 13.5, ...extra };
}
export function MonoLabel({ children }: { children?: ReactNode }) {
  return <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--di-label)" }}>{children}</span>;
}

export interface DefectDraft {
  title: string;
  category: string;
  severity: Severity;
  desc: string;
}

export function DefectForm({ draft, setDraft, defect }: {
  draft: DefectDraft;
  setDraft: (d: DefectDraft) => void;
  defect?: Defect | null;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 15 }}>
      {defect && defect.source === "ai" && defect.confidence != null &&
        <div style={{ padding: "11px 13px", borderRadius: 10, background: "var(--di-navy)", border: "1px solid var(--di-border)" }}>
          <ConfidenceMeter value={defect.confidence} />
        </div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <MonoLabel>Title</MonoLabel>
        <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="e.g. Corrosion" style={fieldBox()} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <MonoLabel>Type</MonoLabel>
        <CatSelect value={draft.category || "other"} setValue={(c) => setDraft({ ...draft, category: c })} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <MonoLabel>Severity</MonoLabel>
        <div style={{ display: "flex", gap: 6 }}>
          {SEV_ORDER.slice().reverse().map((s) => {
            const on = draft.severity === s;
            const m = SEVERITY[s];
            return <button key={s} onClick={() => setDraft({ ...draft, severity: s })}
              style={{ flex: 1, padding: "8px 4px", borderRadius: 8, border: `1.5px solid ${on ? m.color : "var(--di-border)"}`, background: on ? m.bg : "var(--di-navy)", color: on ? m.color : "var(--di-muted)", cursor: "pointer", fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{m.label}</button>;
          })}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        <MonoLabel>Description</MonoLabel>
        <textarea value={draft.desc} onChange={(e) => setDraft({ ...draft, desc: e.target.value })} rows={4} placeholder="Describe what is observed…" style={fieldBox({ resize: "vertical", lineHeight: 1.55, minHeight: 90 })} />
      </div>
    </div>
  );
}
